import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, NumberInput, Textarea, TextInput,} from "@mantine/core";
import {useCallback} from "react";
import {useServiceEventEditForm} from "./useServiceEventEditForm";
import {ServiceEventCloseoutPolicy, toServiceEventCloseoutPolicy} from "@ots-frontend/ots-data";
import {DateInput} from "../../date/DateInput/DateInput";
import {
  ServiceEventCloseoutPolicySelect
} from "../../select/entity/ServiceEventCloseoutPolicySelect/ServiceEventCloseoutPolicySelect";
import {ServiceEventFormDefaultValues} from "./ServiceEventForm";

export interface ServiceEventEditFormProps {
  options?: ServiceEventEditFormOptions;
  defaultValues?: ServiceEventFormDefaultValues;
  onSubmit?: (values: ServiceEventEditFormValues) => void;
  submitting?: boolean;
}

export interface ServiceEventEditFormOptions {

}

export interface ServiceEventEditFormValues {
  name: string
  severityLevel?: number
  startDate: Date
  endDate?: Date
  closeoutPolicy: ServiceEventCloseoutPolicy
  note?: string
}

export interface ServiceEventEditFormRawValues {
  name?: string
  severityLevel?: number
  startDate?: Date
  endDate?: Date
  closeoutPolicy?: string
  note?: string
}

export type ServiceEventEditFormType = UseFormReturnType<ServiceEventEditFormRawValues, (values: ServiceEventEditFormRawValues) => ServiceEventEditFormRawValues>

export const ServiceEventEditForm = (props: ServiceEventEditFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false,
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useServiceEventEditForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        name: values.name!,
        severityLevel: values.severityLevel,
        startDate: values.startDate!,
        endDate: values.endDate,
        closeoutPolicy: toServiceEventCloseoutPolicy(values.closeoutPolicy!),
        note: values.note,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            size="xs"
            label={t("common.entity.serviceevent.form.field.name", "Name")}
            placeholder={t<string>("common.entity.serviceevent.form.field.name", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider my="sm" variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <NumberInput
            size="xs"
            label={t("common.entity.serviceevent.form.field.severitylevel", "Severity Level")}
            placeholder={t<string>("common.entity.serviceevent.form.field.severitylevel", "Severity Level")}
            step={1}
            min={0}
            max={5}
            {...form.getInputProps("severityLevel")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider my="sm" variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            size="xs"
            label={t("common.entity.serviceevent.form.field.start", "Start")}
            placeholder={t<string>("common.entity.serviceevent.form.field.type.start", "Start")}
            valueFormat="MM/DD/YYYY HH:mm"
            withAsterisk
            required
            clearable
            styles={{wrapper: {width: "100%"}}}
            {...form.getInputProps("startDate")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            size="xs"
            label={t("common.entity.serviceevent.form.field.end", "End")}
            placeholder={t<string>("common.entity.serviceevent.form.field.type.end", "End")}
            valueFormat="MM/DD/YYYY HH:mm"
            clearable
            styles={{wrapper: {width: "100%"}}}
            {...form.getInputProps("endDate")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <ServiceEventCloseoutPolicySelect
            size="xs"
            label={t("common.entity.serviceevent.form.field.closeoutpolicy", "Closeout Policy")}
            placeholder={t<string>("common.entity.serviceevent.form.field.closeoutpolicy", "Closeout Policy")}
            clearable
            withAsterisk
            required
            {...form.getInputProps("closeoutPolicy")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider my="sm" variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            size="xs"
            label={t("common.entity.serviceevent.form.field.note", "Note")}
            placeholder={t<string>("common.entity.serviceevent.form.field.note.placeholder", "Note")}
            {...form.getInputProps("note")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};
